import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Select from 'react-select';
import Loading from 'components/Loading';
import {
    getShows,
    getFilterOptions,
    selectors,
    setFilter,
} from '../../ducks/shows';
import { gettext } from '../../utils/text';
import SingleShow from './SingleShow';
import { OptionShape, ShowShape } from './types';
import DropdownIndicator from '../DropdownIndicator';

class HomepageWrapper extends React.Component {
    static propTypes = {
        shows: PropTypes.arrayOf(ShowShape).isRequired,
        filters: PropTypes.instanceOf(Object).isRequired,
        stagings: PropTypes.arrayOf(OptionShape).isRequired,
        periods: PropTypes.arrayOf(OptionShape).isRequired,
        stagingTypes: PropTypes.arrayOf(OptionShape).isRequired,
        getFilterValue: PropTypes.func.isRequired,
        getShows: PropTypes.func.isRequired,
        getFilterOptions: PropTypes.func.isRequired,
        applyFilter: PropTypes.func.isRequired,
        initialLoading: PropTypes.bool.isRequired,
        nextLoading: PropTypes.bool.isRequired,
        nextPage: PropTypes.string,
    };

    static defaultProps = {
        nextPage: null,
    };

    constructor(props) {
        super(props);
        this.state = { pageNumber: 1 };
        this.showsContainerRef = React.createRef();
        this.shouldFocusNewPageFirstItem = false;
        this.prevShowsLength = 0;
    }

    componentDidMount() {
        this.props.getShows(this.props.filters);
        this.props.getFilterOptions(this.props.filters);
    }

    componentDidUpdate(prevProps, _) {
        // When new shows are appended after clicking Next page, move focus
        if (
            this.shouldFocusNewPageFirstItem &&
            !this.props.nextLoading &&
            (prevProps.nextLoading ||
                this.props.shows.length > prevProps.shows.length)
        ) {
            const container = this.showsContainerRef.current;
            if (container) {
                const showElems = container.querySelectorAll('.__show');
                const focusIndex = this.prevShowsLength;
                if (showElems && showElems[focusIndex]) {
                    try {
                        const linkToFocus = showElems[focusIndex].querySelector(
                            'a',
                        );
                        if (linkToFocus) {
                            linkToFocus.focus();
                            if (linkToFocus.scrollIntoView) {
                                linkToFocus.scrollIntoView({
                                    block: 'nearest',
                                });
                            }
                        }
                    } catch (e) {
                        // no-op: focusing is best-effort
                    }
                }
            }
            this.shouldFocusNewPageFirstItem = false;
        }
    }

    onFilterChange = (filter, option) => {
        this.props.applyFilter(filter, option);
        const newFilter = { ...this.props.filters, upcomingShowsPageNumber: 1 };
        newFilter[filter] = option;
        // on filter change load shows from beginning
        this.props.getShows(newFilter, true);
        this.props.getFilterOptions(newFilter);
    };

    getDefaultCustomStyles = () => ({
        control: (provided, _state) => ({
            ...provided,
            border: '0 none',
            marginLeft: 40,
            marginTop: 1,
        }),
    });

    getNextPage = () => {
        const nextPageNumber = this.state.pageNumber + 1;
        this.setState({ pageNumber: nextPageNumber }, () => {
            this.prevShowsLength = this.props.shows.length;
            this.shouldFocusNewPageFirstItem = true;
            const newFilter = {
                ...this.props.filters,
                upcomingShowsPageNumber: this.state.pageNumber,
            };
            this.props.applyFilter(
                'upcomingShowsPageNumber',
                this.state.pageNumber,
            );
            this.props.getShows(newFilter);
        });
    };

    renderShows() {
        if (this.props.initialLoading) {
            return <Loading />;
        }
        let renderedShows = (
            <h2>{gettext('No results matching selected filters.')}</h2>
        );

        if (this.props.shows.length) {
            renderedShows = this.props.shows.map(show => {
                const key = `${show.id}-${show.staging.id}`;
                return <SingleShow show={show} key={key} />;
            });
        }

        return (
            <>
                <ul
                    className="__shows list-unstyled"
                    ref={this.showsContainerRef}
                >
                    {renderedShows}
                </ul>
                {this.renderUpcomingShowsNextButton()}
            </>
        );
    }

    renderUpcomingShowsNextButton = () => {
        if (this.props.nextLoading) {
            return <Loading />;
        }
        // Display the next page button only if the upcoming shows option is selected,
        // if the next page is present
        const showUpcomingShowsNextButton =
            this.props.getFilterValue('period').value === 1 &&
            this.props.nextPage;
        if (showUpcomingShowsNextButton) {
            return (
                <div className="__next-page-div">
                    <button
                        className="btn btn-primary "
                        type="button"
                        onClick={() => {
                            this.getNextPage();
                        }}
                    >
                        {gettext('Next page')}
                    </button>
                </div>
            );
        }
        return null;
    };

    renderStagingSelector() {
        return (
            <Select
                inputId="staging-filter-select"
                value={this.props.getFilterValue('staging')}
                onChange={option => this.onFilterChange('staging', option)}
                components={{ DropdownIndicator }}
                options={this.props.stagings}
                className="react-select menu-outer-top"
                classNamePrefix="react-select"
                styles={this.getDefaultCustomStyles()}
                isSearchable={false}
                aria-label={gettext('Filter by staging')}
                placeholder={gettext('Select staging')}
            />
        );
    }

    renderPeriodSelector() {
        return (
            <Select
                inputId="period-filter-select"
                value={this.props.getFilterValue('period')}
                onChange={option => this.onFilterChange('period', option)}
                components={{ DropdownIndicator }}
                options={this.props.periods}
                className="react-select"
                classNamePrefix="react-select"
                styles={this.getDefaultCustomStyles()}
                isSearchable={false}
                aria-label={gettext('Filter by period')}
                placeholder={gettext('Select period')}
            />
        );
    }

    renderStagingTypeSelector() {
        return (
            <Select
                inputId="staging-type-filter-select"
                value={this.props.getFilterValue('stagingType')}
                onChange={option => this.onFilterChange('stagingType', option)}
                components={{ DropdownIndicator }}
                options={this.props.stagingTypes}
                className="react-select"
                classNamePrefix="react-select"
                styles={this.getDefaultCustomStyles()}
                isSearchable={false}
                aria-label={gettext('Filter by staging type')}
                placeholder={gettext('Select staging type')}
            />
        );
    }

    render() {
        return (
            <>
                <div className="__filters">
                    <div className="__filter-group">
                        <label
                            className="__filter-label"
                            htmlFor="staging-filter-select"
                        >
                            {gettext('Staging')}
                        </label>
                        <div className="__filter __stagings">
                            {this.renderStagingSelector()}
                        </div>
                    </div>
                    <div className="__filter-group">
                        <label
                            className="__filter-label"
                            htmlFor="period-filter-select"
                        >
                            {gettext('Period')}
                        </label>
                        <div className="__filter __dates">
                            {this.renderPeriodSelector()}
                        </div>
                    </div>
                    <div className="__filter-group">
                        <label
                            className="__filter-label"
                            htmlFor="staging-type-filter-select"
                        >
                            {gettext('Staging type')}
                        </label>
                        <div className="__filter __scenes">
                            {this.renderStagingTypeSelector()}
                        </div>
                    </div>
                </div>
                {this.renderShows()}
            </>
        );
    }
}

const mapStateToProps = state => ({
    shows: selectors.shows(state),
    filters: selectors.filters(state),
    stagings: selectors.stagings_options(state),
    periods: selectors.period_options(state),
    stagingTypes: selectors.staging_type_options(state),
    activeStagingSelection: selectors.staging(state),
    getFilterValue: filter => selectors.getSelectedFilter(state, filter),
    initialLoading: state.shows.initialLoading,
    nextLoading: state.shows.nextLoading,
    nextPage: selectors.nextPage(state),
});

const mapDispatchToProps = dispatch => ({
    getShows: (filters, reset) => dispatch(getShows(filters, reset)),
    getFilterOptions: filters => dispatch(getFilterOptions(filters)),
    applyFilter: (key, value) => {
        dispatch(setFilter(key, value));
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(HomepageWrapper);
